import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';
import type { PaymentMethod, PaymentRequirementTarget } from '@pta/types';
import { createPayment } from '../api/payments';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';
import { useMoneyFormatter } from '../util/money';

/**
 * F2e — Gyors "teljes hátralék" befizetés modal.
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
interface QuickPaymentConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  target: PaymentRequirementTarget;
  studentName: string;
  currency: string;
  /** A teljes hátralék (target.expectedAmount − paidSoFar). NUMERIC string. */
  remaining: string;
  onSaved: () => void;
}

export function QuickPaymentConfirmModal({
  opened,
  onClose,
  groupId,
  target,
  studentName,
  currency,
  remaining,
  onSaved,
}: QuickPaymentConfirmModalProps) {
  const { money } = useMoneyFormatter();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  void currency;

  useEffect(() => {
    if (opened) {
      setPaymentMethod('cash');
      setNotes('');
    }
  }, [opened]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await createPayment(groupId, {
        targetId: target.id,
        amount: remaining,
        paidAt: toIsoDate(new Date()),
        paymentMethod,
        externalRef: null,
        notes: notes.trim() || null,
      });
      notifications.show({
        color: 'green',
        message: `${studentName}: ${money(remaining)} rögzítve.`,
      });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Teljes hátralék befizetése"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="md"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Stack gap={2}>
            <Text size="sm">
              <strong>Diák:</strong> {studentName}
            </Text>
            <Text size="sm">
              <strong>Összeg:</strong>{' '}
              <Badge color="teal" variant="light" size="lg">
                {money(remaining)}
              </Badge>
            </Text>
            <Text size="sm">
              <strong>Dátum:</strong> {toIsoDate(new Date())} (mai nap)
            </Text>
          </Stack>
        </Alert>

        <Radio.Group
          label="Fizetési mód"
          value={paymentMethod}
          onChange={(v) => setPaymentMethod(v as PaymentMethod)}
        >
          <Group mt="xs">
            <Radio value="cash" label="Készpénz" />
            <Radio value="bank_transfer" label="Banki átutalás" />
          </Group>
        </Radio.Group>

        <Textarea
          label="Megjegyzés (opcionális)"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={1}
          maxLength={2000}
        />

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            color="teal"
            onClick={handleSave}
            loading={saving}
          >
            Befizetés rögzítése
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
